<script setup lang="ts">
import type { Condo } from '~/core/entities/condo/Condo';
import type { CondoFilters } from '~/core/entities/condo/Filters';

import { CACHE_TIME } from '~/utils';
import { nuxtCondosRepository } from '~~/adapters/repositories/nuxt';
import { CONDO_DETAILS } from '~~/utils/symbols';

const condominium = inject(CONDO_DETAILS);
const sectionName = 'condos around';

const key = computed(() => {
	const normalizedCondoName = normalizeString(condominium?.value?.condo.name);

	return `condominium:surrounding-condos-${normalizedCondoName}`;
});

const query = computed<CondoFilters>(
	() =>
		({
			lat: condominium?.value?.condo.lat,
			lng: condominium?.value?.condo.lng,
			condoType: condominium?.value?.condo.condoType['identifier'],
			excludeCanonicalCondo: [condominium?.value?.condo.id ?? '']
		}) as const satisfies CondoFilters
);

const defaultCondos = { payload: [] as Condo[] };

const { data: condos, status: status } = useAsyncData(
	key.value,
	async () => {
		try {
			const filters = {
				page: 1,
				perPage: 12,
				radius: 2000,
				...query.value
			};

			const [result, error] = await nuxtCondosRepository.list(filters);

			if (error || !result?.condos?.length) {
				return defaultCondos;
			}

			return {
				payload: result.condos
			};
		} catch {
			return defaultCondos;
		}
	},
	{
		default: () => defaultCondos,
		lazy: true,
		...getCacheAsyncDataConfig<{ payload: Condo[] }>(CACHE_TIME),
		watch: [() => condominium?.value?.condo]
	}
);

const isLoadingProperties = computed(() => {
	return status.value === 'pending';
});

const title = computed(() => {
	const name = condominium?.value?.condo.name;

	return `Condomínios próximos ao ${name}`;
});

const isEmpty = computed(() => {
	return condos.value.payload.length === 0;
});
</script>

<template>
	<StandardSlider
		v-if="!isEmpty"
		:items="condos.payload"
		:title="title"
		:is-loading="isLoadingProperties"
		:section-name="sectionName"
		title-as="h3"
	>
		<template #items="{ item, index }">
			<NuxtLink
				:to="`/condominio/${item.slug}`"
				target="_blank"
				rel="noopener noreferrer"
			>
				<CondoCard
					:condo="item"
					:section="sectionName"
					variant="whiteBackground"
					:card-position="index"
				/>
			</NuxtLink>
		</template>
		<template #cta></template>
	</StandardSlider>
</template>
